import React, {useEffect, useMemo, useState} from 'react'
import TableHeader from '../../../../components/table/TableHeader'
import Text from '../../../../components/text/Text'
import TableLayout from '../../../../components/table/TableLayout'
import useQueryEvent from '../../../../hooks/api/event/useQueryEvent'
import TableItem from '../../../../components/table/TableItem'
import WizImage from '../../../../components/image/WizImage'
import {IEvent} from '../../../../apis/event/getEventApi'
import useNavigation from '../../../../hooks/useNavigation'
import {useTime} from '../../../../utils/time'
import {updateEvent} from '../../../../apis/event/updateEvent'
import WizPagination from '../../../../components/pagination/WizPagination'
import {useTag} from '../../../../utils/etc'

interface IProps {
    className?: string
    active?: boolean
    priorityActive?: boolean
}

const NoneImage = () => (
    <div className={'w-[120px] h-[120px] leading-[120px] bg-line01 text-center text-white rounded-[10px]'}>
        이미지 없음
    </div>
)

const isActvie = (startAt: string, endAt: string, priority: number): string => {
    if (!startAt && !endAt) {
        if (priority > 0) {
            return 'Live'
        } else {
            return 'none'
        }
    }

    const now = new Date()
    const startD = new Date(startAt)
    const endD = new Date(endAt)
    if (now > endD) {
        return 'Ended'
    } else if (now < startD) {
        return 'Soon'
    } else if (now > startD || now < endD) {
        return 'Live'
    }
}

const EventTable: React.FC<IProps> = ({className, active, priorityActive}) => {
    const [currentPage, setCurrentPage] = useState(1)
    const {data, pageInfo, refetch} = useQueryEvent(currentPage, active)
    const {goEventDetail} = useNavigation()
    const {renderEventTagColor} = useTag()
    const {convertUtc0ToUtc9} = useTime()

    const eventList = useMemo(() => {
        const list: IEvent[] = []
        data?.map(item => list?.push(item))
        list.sort((a, b) => b?.priority - a?.priority)
        return list
    }, [data])

    const [list, setList] = useState(eventList)
    const handleInputChange = async (id: string, newValue: number) => {
        if (newValue < 0) {
            return
        }
        let target: IEvent = undefined

        setList(prevList =>
            prevList.map(item => {
                if (item.id === id) {
                    target = item
                    return {...item, priority: newValue}
                } else {
                    return item
                }
            }),
        )
        if (target === undefined) {
            return
        }
        const {data} = await updateEvent(id, {...target, priority: newValue})
        if (!data?.success) {
            alert('업데이트가 실패하였습니다')
        } else {
            refetch()
        }
    }

    useEffect(() => {
        refetch()
    }, [currentPage])

    useEffect(() => {
        setList(eventList)
    }, [eventList])
    return (
        <div className={`${className}`}>
            <TableHeader className={'grid-cols-[180px_1fr_100px_100px_200px] gap-x-[10px] md:hidden'}>
                <Text>Thumbnail</Text>
                <Text>Title</Text>
                <Text className={'text-center'}>Live</Text>
                <Text className={'text-center'}>Priority</Text>
                <Text className={'text-center'}>Date</Text>
            </TableHeader>
            <TableLayout>
                {list?.map((item, i) => (
                    <TableItem
                        className={
                            'grid-cols-[180px_1fr_100px_100px_200px] gap-x-[10px] md:grid-cols-[200px_80px_50px_60px] md:justify-center md:gap-y-[30px] md:space-y-0 md:items-start sm:block sm:space-y-[15px]'
                        }
                        onClick={() => goEventDetail(item?.id)}>
                        <div className={'flex-col flex gap-y-[5px] items-center  md:col-span-4 md:justify-center'}>
                            {item?.new_thumbnail || item?.thumbnail ? (
                                <WizImage
                                    src={
                                        item?.new_thumbnail
                                            ? `${item?.new_thumbnail?.split('?')?.at(0)}?type=webp&width=1920`
                                            : `${item?.thumbnail?.split('?')?.at(0)}?type=webp&width=1920` ||
                                              '/lot/logo.png'
                                    }
                                    className={'w-[180px] rounded-[3px] sm:w-[260px] sm:rounded-[10px]'}
                                />
                            ) : (
                                <NoneImage />
                            )}
                            {item?.priority > 0 && item?.show_on_account_page && item?.thumbnail ? (
                                <WizImage
                                    src={`${item?.thumbnail?.split('?')?.at(0)}?type=webp&width=1920`}
                                    className={'w-full rounded-[3px]'}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className={'truncate md:hidden'}>
                            <Text className={'text-ti2 truncate'}>{item?.name}</Text>
                        </div>
                        <div className="flex items-center justify-center space-x-[0px] md:space-x-0 md:space-y-[5px] md:block ">
                            <Text className={'text-ti3 text-font03 text-center hidden md:block'}>Live</Text>
                            <div
                                className={`flex items-center justify-center mx-auto gap-x-[5px] ${renderEventTagColor(
                                    item?.priority > 0 ? 'Live' : 'Closed',
                                )}`}>
                                {item?.priority > 0 ? <div className={'w-[5px] h-[5px] rounded-full bg-white'} /> : ''}
                                <Text
                                    className={`!text-ti2 font-bold w-min rounded-[5px] text-inherit whitespace-nowrap md:mx-auto`}>
                                    {item?.priority > 0 ? 'Live' : 'Closed'}
                                </Text>
                            </div>
                        </div>
                        <div className="flex flex-row h-10 w-full relative bg-transparent mt-1 md:mt-0 md:space-y-[5px] md:block ">
                            <Text className={'text-ti3 text-center hidden md:block'}>Priority</Text>
                            <input
                                disabled={!priorityActive}
                                type="number"
                                className={`focus:outline-none w-full font-semibold text-center bg-transparent text-md hover:text-black focus:text-black md:text-base cursor-default flex items-center text-gray-700 outline-none md:text-center ${
                                    priorityActive ? 'border-b border-b-line01' : ''
                                }`}
                                onClick={e => e.stopPropagation()}
                                onChange={e => handleInputChange(item.id, Number(e.target.value))}
                                value={item.priority}
                            />
                        </div>

                        <div className={'space-y-[5px] sm:text-center'}>
                            <Text className={'text-ti3 text-font03 text-center hidden md:block'}>Date</Text>
                            {item?.start_at || item?.end_at ? (
                                <div>
                                    <Text className={'text-body3 text-center text-font02'}>
                                        Start At : {item?.start_at ? convertUtc0ToUtc9(item?.start_at) : '-'}
                                    </Text>
                                    <Text
                                        className={`text-center ${
                                            new Date()?.getTime() > new Date(item?.end_at)?.getTime()
                                                ? 'text-ti3 text-red-500'
                                                : 'text-body3 text-font02'
                                        }`}>
                                        End At : {item?.end_at ? convertUtc0ToUtc9(item?.end_at) : ''}
                                    </Text>
                                </div>
                            ) : (
                                <Text className={'text-ti2 text-center'}>All Times</Text>
                            )}
                        </div>
                    </TableItem>
                ))}
            </TableLayout>
            <WizPagination
                totalPage={pageInfo?.total_count}
                currentPage={currentPage}
                onPageChange={item => setCurrentPage(item)}
            />
        </div>
    )
}

export default EventTable
